import React from "react";
import { LocalUser, RemoteUser } from "agora-rtc-react";
import { useNavigate } from "react-router-dom";

import { BsFillMicMuteFill, BsFillMicFill } from "react-icons/bs";
import { ImPhoneHangUp } from "react-icons/im";
import { VscLoading } from "react-icons/vsc";

import { useScheduleCall } from "@/hooks/use-schedule-call";
import { useUnauthorizedRedirect } from "@/hooks/use-unauthorized-redirect";
import { useAuth } from "@/hooks/use-auth";
import SessionTimer from "@/components/SessionTimer";

/**
 * Scheduled voice call screen (partner-app side).
 *
 * Mirrors the video screen but stripped to audio essentials. Per the
 * D1 partner-app billing wiring:
 *   - meeting/billing come from the hook (fetched via /api/meeting/by-id)
 *   - endCall handles billing-end + meeting completion + Agora teardown
 *   - hangup tap calls endCall("astrologer_ended"), then navigates
 *
 * Previously this screen had no server-end path at all — only
 * setIsUserReady(false), which left the Meeting stuck in 'pending'
 * and the billing session running until the orphan sweeper killed it
 * minutes later. Both leaks closed here.
 */
export default function ScheduleVoiceCallPage() {
  useUnauthorizedRedirect();
  const navigate = useNavigate();
  const { astrologer } = useAuth();

  const {
    localMicrophoneTrack,
    micOn,
    remoteUsers,
    setMic,
    isConnected,
    isUserReady,
    isPageReady,
    isLoading,

    // D1 partner-app billing wiring
    meeting,
    billing,
    endCall,
  } = useScheduleCall("voice");

  const userName = meeting?.user?.name || "User";
  const astrologerName =
    astrologer?.personalInformation?.name || astrologer?.name || "Astrologer";

  const handleEndCall = async () => {
    await endCall("astrologer_ended");
    navigate("/connect/meeting-end");
  };

  if (!isPageReady || isLoading) {
    return (
      <div className="flex bg-black justify-center items-center w-full h-screen flex-col relative">
        <VscLoading size={50} color="#f16631" className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center w-full h-screen flex-col relative bg-gray-50">
      {/* Session timer + earnings tile, anchored top-right */}
      <div className="absolute top-4 right-4 z-30 flex flex-col items-end gap-2">
        <SessionTimer
          isConnected={isConnected}
          isUserReady={isUserReady}
          onDurationChange={() => {}}
        />
        {billing?.isActive && billing?.ratePerMinute > 0 && (
          <div className="rounded-lg bg-white px-3 py-1.5 text-right shadow-sm border border-orange-200">
            <div className="text-[10px] font-bold uppercase tracking-widest text-orange-600">
              Earned
            </div>
            <div className="text-base font-semibold text-gray-800">
              ₹{Math.round(billing.totalBilledAmount || 0)}
            </div>
            <div className="text-[10px] text-gray-500">
              ₹{billing.ratePerMinute}/min
            </div>
          </div>
        )}
      </div>

      <div className="w-full flex-1 flex justify-center items-center pt-1 relative">
        <RemoteUser
          playAudio={false}
          playVideo={false}
          user={remoteUsers[0]}
          className="rounded-xl"
          cover={() => (
            <div className="w-full h-full justify-center items-center flex bg-[#eee] flex-col gap-4">
              <div className="text-white flex justify-center items-center w-32 aspect-square bg-pink-500 rounded-full">
                <p className="text-5xl">
                  {userName.charAt(0).toUpperCase()}
                </p>
              </div>
              <div className="text-lg font-medium text-gray-800">
                {userName}
              </div>
              <div className="text-xs text-gray-500">
                Voice call with {astrologerName}
              </div>
            </div>
          )}
        />
        <div className="invisible">
          <LocalUser
            playVideo={false}
            playAudio={false}
            audioTrack={localMicrophoneTrack}
            micOn={micOn}
            videoTrack={null}
            cameraOn={false}
          />
        </div>

        {/* Bottom controls */}
        <div className="flex justify-center items-center mt-3 absolute z-10 bottom-8">
          <div>
            <button
              className="text-3xl rounded-full p-3 text-white bg-zinc-800 mr-3"
              onClick={() => setMic((prev) => !prev)}
            >
              {micOn ? (
                <BsFillMicFill size={18} />
              ) : (
                <BsFillMicMuteFill color="red" size={18} />
              )}
            </button>
            {isConnected && (
              <button
                onClick={handleEndCall}
                className="text-white rounded-full p-3 text-2xl bg-zinc-800"
              >
                <ImPhoneHangUp color="red" size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
